import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alert from '../components/Alert';
import Spinner from '../components/Spinner';
import BCPPageTitle from '../components/BCPPageTitle';
import GuideTopAI from '../components/GuideTopAI';
import RedButton from '../components/RedButton';
import BasicBCPStepFlow from '../components/BasicBCPStepFlow';
import Button from '../parts/button';
import BCPDownloadModal from '../components/BCPDownloadModal';
import { navigationService } from '../utils/navigation';
import { exportBCP } from '../services/api';
import { useLoadingState } from '../hooks/useLoadingState';
import { useRestorePolicyGenerationAsync } from '../hooks/useRestorePolicyGenerationAsync';
import { useAutoDismissMessage } from '../hooks/useAutoDismissMessage';
import { useBCPLoader } from '../hooks/useBCPLoader';
import { useAIConsultContext } from '../hooks/useAIConsultContext';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import { bcpService } from '../services/bcpService';
import type { BCPSaveRequest, BCPUpdateRequest } from '../types/bcp';
import {
  BCPFormContainer,
  BCPFormLayout,
  BCPSectionCard,
  ReferenceMaterialsPanel,
} from '../components/BCP';
import { RESTORE_POLICY_SECTIONS } from '../config/restorePolicySections';
import { useBCPDynamicForm } from '../hooks/useBCPDynamicForm';
import {
  computeUpdatedFields,
  extractImportantBusinesses,
} from '../utils/importantBusinessSync';
import { saveBasicBcpSection, saveAllDraftSections, loadBasicBcpSection } from '../utils/basicBcpStorage';

interface NavigationState {
  uploadedFiles: any[];
  apiResponse?: any;
  restorePolicyFormData?: Record<string, string>;
  companyInfo?: {
    companyName: string;
    location: string;
    employeeCount?: string;
    businessDescription: string;
    criticalBusiness: string;
  };
  successMessage?: string;
  bcpId?: string;
}

const RESTORE_POLICY_FIELD_IDS = RESTORE_POLICY_SECTIONS.flatMap((section) =>
  section.fields
    .filter((field) => field.type !== 'static')
    .map((field) => field.id)
);

const extractRestorePolicyFormValues = (
  source?: Record<string, any>
): Record<string, string> => {
  if (!source) {
    return {};
  }

  const result: Record<string, string> = {};

  RESTORE_POLICY_FIELD_IDS.forEach((fieldId) => {
    const value = source[fieldId];
    if (typeof value === 'string' && value.trim().length > 0) {
      result[fieldId] = value;
    }
  });

  // Backward compatibility: migrate legacy important_business to restore_policy_important_business
  if (!result.restore_policy_important_business && source.important_business) {
    const legacyValue = source.important_business;
    if (typeof legacyValue === 'string' && legacyValue.trim().length > 0) {
      result.restore_policy_important_business = legacyValue;
    }
  }

  return result;
};

const RestorePolicyPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ bcpId?: string }>();
  const [searchParams] = useSearchParams();

  const navigationState = navigationService.extractLocationState<NavigationState>(
    location,
    {
      uploadedFiles: [],
      apiResponse: undefined,
      restorePolicyFormData: undefined,
      companyInfo: undefined,
    }
  );

  const { uploadedFiles, apiResponse, companyInfo } = navigationState;

  const exportState = useLoadingState<Blob>();
  const [saveLoading, setSaveLoading] = useState(false);
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const { message: successMessage, showMessage: setSuccessMessage } =
    useAutoDismissMessage();
  const hasLoadedInitialData = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // ページロード時に上部にスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // isDirtyの変更を監視
  useEffect(() => {
    console.log('[RestorePolicyPage] isDirty changed to:', isDirty);
  }, [isDirty]);

  const bcpIdFromPath = params.bcpId ?? 'new';
  const tenantIdFromQuery = searchParams.get('tenantId');
  // navigation stateのbcpIdを優先使用（「戻る」ボタンから正しいbcpIdを受け取るため）
  const resolvedBcpId = navigationState.bcpId || bcpIdFromPath;
  const resolvedTenantId = tenantIdFromQuery ?? apiResponse?.tenant_id ?? null;

  // BCP読み込み(編集モード判定のため先に実行)
  const {
    loading: bcpLoading,
    isEditMode,
    bcpDocument,
    getInitialFormData,
    reload: reloadBCP,
  } = useBCPLoader({
    bcpId: resolvedBcpId === 'new' ? null : resolvedBcpId,
    tenantId: resolvedTenantId,
    onError: (error) => {
      setGeneralErrors([error]);
    },
  });

  // 編集モードまたはBCP読み込み中の場合は自動生成をスキップ
  const shouldSkipAutoGeneration = bcpLoading || isEditMode;

  // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
  const isNavigatingRef = useRef(false);

  // ページ遷移時にisNavigatingRefをリセット
  useEffect(() => {
    isNavigatingRef.current = false;
  }, [location.pathname, location.search]);

  // 離脱確認ダイアログ(フォームが変更されていない、またはエクスポート中はスキップ)
  useBeforeUnload({
    enabled: true,
    shouldBypass: () => {
      // 「次へ」「戻る」ボタンでのナビゲーション中はブロックしない
      if (isNavigatingRef.current) return true;

      // 新規BCP作成時、またはdraftステータスのBCPは、編集していなくても離脱時に確認ダイアログを表示
      if (resolvedBcpId === 'new' || bcpDocument?.status === 'draft') {
        const shouldSkip = isSaving || exportState.isLoading;
        console.log('[RestorePolicyPage] shouldBypass check (new/draft BCP):', {
          resolvedBcpId,
          status: bcpDocument?.status,
          isSaving,
          isLoading: exportState.isLoading,
          shouldSkip
        });
        return shouldSkip;
      }

      // 公開済みBCP編集時は、編集している場合のみダイアログを表示
      const shouldSkip = !isDirty || isSaving || exportState.isLoading;
      console.log('[RestorePolicyPage] shouldBypass check (published BCP):', { isDirty, isSaving, isLoading: exportState.isLoading, shouldSkip });
      return shouldSkip;
    },
  });

  const {
    isLoading: generationLoading,
    formData: generatedFormData,
    hasGenerated,
    error: generationError,
    jobStatus,
    elapsedSeconds,
    regenerate,
  } = useRestorePolicyGenerationAsync({
    uploadedFiles,
    apiResponse,
    companyInfo,
    savedFormData: navigationState.restorePolicyFormData,
    skipAutoGeneration: shouldSkipAutoGeneration,
    pollIntervalMs: 2000,
    maxPollTimeMs: 300000,
    maxSearchResults: 5,
    tenantId: resolvedTenantId ?? 'default',
  });

  // Migrate legacy important_business to restore_policy_important_business in navigation state
  const migrateNavigationData = (data?: Record<string, string>) => {
    if (!data) return data;
    const migrated = { ...data };
    if (migrated.important_business && !migrated.restore_policy_important_business) {
      migrated.restore_policy_important_business = migrated.important_business;
      delete migrated.important_business;
    }
    return migrated;
  };

  const initialValuesRef = useRef<Record<string, string> | undefined>(
    migrateNavigationData(navigationState.restorePolicyFormData)
  );

  const form = useBCPDynamicForm({
    sections: RESTORE_POLICY_SECTIONS,
    initialValues: initialValuesRef.current,
  });

  const { values, errors: fieldErrors, setFieldValue, setValues, reset, validate } =
    form;

  // valuesの変更を監視してisDirtyを設定
  const previousValuesRef = useRef<Record<string, string>>({});
  const currentValuesRef = useRef<Record<string, string>>(values);
  const isReadyToTrackRef = useRef(false);
  const locationKeyRef = useRef(location.key);
  const shouldResetTrackingRef = useRef(false);

  // 常に最新のvaluesをrefに保存
  useEffect(() => {
    currentValuesRef.current = values;
  }, [values]);

  // データロード完了後にpreviousValuesRefをリセット
  useEffect(() => {
    if (shouldResetTrackingRef.current) {
      previousValuesRef.current = values;
      currentValuesRef.current = values;
      console.log('[RestorePolicyPage] Reset tracking refs after data load');
      shouldResetTrackingRef.current = false;
    }
  }, [values]);

  // ページ遷移を検知して状態をリセット
  useEffect(() => {
    if (location.key !== locationKeyRef.current) {
      console.log('[RestorePolicyPage] Page navigation detected, resetting tracking state');
      locationKeyRef.current = location.key;
      hasLoadedInitialData.current = false;
      isReadyToTrackRef.current = false;
      setIsDirty(false);
    }
  }, [location.key]);

  // 初期データロード完了後、監視を開始
  useEffect(() => {
    if (isReadyToTrackRef.current) {
      return; // すでに監視開始済み
    }

    // 編集モードまたはAI生成モードの場合、初期データロードを待つ
    const isDataReady = (isEditMode || hasGenerated) ? hasLoadedInitialData.current : true;

    // ロードが完了していることを確認
    if (!generationLoading && !bcpLoading && isDataReady) {
      // 少し遅延させて、データ設定が完了するのを待つ
      const timer = setTimeout(() => {
        console.log('[RestorePolicyPage] Initializing change tracking');
        previousValuesRef.current = currentValuesRef.current;
        isReadyToTrackRef.current = true;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [generationLoading, bcpLoading, isEditMode, hasGenerated]);

  // valuesの変更を監視
  useEffect(() => {
    // 監視準備ができていない場合はスキップ
    if (!isReadyToTrackRef.current) {
      return;
    }

    // ロード中や保存中はスキップ
    if (generationLoading || bcpLoading || isSaving) {
      previousValuesRef.current = values;
      return;
    }

    // valuesが変更されたかチェック
    const changedKeys = Object.keys(values).filter(
      key => values[key] !== previousValuesRef.current[key]
    );

    if (changedKeys.length > 0) {
      console.log('[RestorePolicyPage] values changed, setting isDirty = true, changed keys:', changedKeys);
      setIsDirty(true);
      previousValuesRef.current = values;
    }
  }, [values, generationLoading, bcpLoading, isSaving]);

  // Auto-save form data to localStorage (debounced)
  useEffect(() => {
    // Skip if not ready or loading
    if (!isReadyToTrackRef.current || generationLoading || bcpLoading) {
      return;
    }

    // Skip if no bcpId or values are empty
    if (resolvedBcpId === 'new' || Object.keys(values).length === 0) {
      return;
    }

    // Save immediately to ensure latest data is available for PDF download
    // No debounce to prevent timing issues with PDF download
    saveBasicBcpSection(resolvedBcpId, resolvedTenantId, 'restore_policy', values);
  }, [values, resolvedBcpId, resolvedTenantId, generationLoading, bcpLoading]);

  const aiConsultContext = useAIConsultContext({
    sections: RESTORE_POLICY_SECTIONS,
    values,
    bcpType: 'basic',
    onFieldValueChange: setFieldValue,
  });

  // AI生成完了後に自動保存されたBCPを保持(セッション内のみ、localStorageは使わない)
  const AUTO_SAVED_BCP_KEY = `autoSavedBcp_${resolvedTenantId || 'default'}`;

  const [autoSavedBcp, setAutoSavedBcp] = useState<any>(null);

  const hasAutoSaved = useRef(false);

  // Handle success message from navigation state (e.g., after save and navigate)
  useEffect(() => {
    if (navigationState.successMessage) {
      setSuccessMessage(navigationState.successMessage);
      setGeneralErrors([]);
    }
  }, [navigationState.successMessage, setSuccessMessage]);

  // AI生成完了時にフォームにデータを設定
  useEffect(() => {
    if (hasGenerated && !isEditMode && Object.keys(generatedFormData).length > 0) {
      // Migrate legacy important_business to restore_policy_important_business for AI-generated data
      const migratedData = { ...generatedFormData };
      if (migratedData.important_business && !migratedData.restore_policy_important_business) {
        migratedData.restore_policy_important_business = migratedData.important_business;
        delete migratedData.important_business;
      }

      reset(migratedData);
      setSuccessMessage(
        'AIによる復旧方針の下書きを生成しました。'
      );
      setGeneralErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGenerated, isEditMode]);

  useEffect(() => {
    if (isEditMode && bcpDocument && !hasLoadedInitialData.current) {
      const rawData = getInitialFormData();
      console.log('[RestorePolicyPage] Raw initial form data:', {
        keys: Object.keys(rawData),
        sampleValues: Object.keys(rawData).slice(0, 3).reduce((acc, key) => {
          acc[key] = rawData[key];
          return acc;
        }, {} as Record<string, any>)
      });

      const initialData = extractRestorePolicyFormValues(
        rawData as Record<string, string>
      );
      console.log('[RestorePolicyPage] Extracted restore policy values:', {
        keys: Object.keys(initialData),
        fieldCount: Object.keys(initialData).length
      });

      // Try to load from localStorage first (highest priority)
      const savedData = loadBasicBcpSection(resolvedBcpId, resolvedTenantId, 'restore_policy');
      const hasSavedData = savedData && Object.keys(savedData).length > 0;

      let dataToLoad = initialData;
      let source = 'bcpDocument';

      if (hasSavedData) {
        // Migrate legacy important_business in localStorage data
        const migratedSavedData = { ...savedData };
        if (migratedSavedData.important_business && !migratedSavedData.restore_policy_important_business) {
          migratedSavedData.restore_policy_important_business = migratedSavedData.important_business;
          delete migratedSavedData.important_business;
        }

        // localStorage と DB データをマージ（localStorageが優先、ただしlocalStorageにないフィールドはDBから取得）
        dataToLoad = { ...initialData, ...migratedSavedData };
        source = 'localStorage (merged with DB)';
      }

      console.log('[RestorePolicyPage] Loading data:', {
        source,
        keys: Object.keys(dataToLoad)
      });

      console.log('[RestorePolicyPage] Before reset - current values:', {
        keys: Object.keys(values),
        count: Object.keys(values).length
      });

      console.log('[RestorePolicyPage] Calling reset with dataToLoad:', dataToLoad);
      reset(dataToLoad);
      console.log('[RestorePolicyPage] Reset called');

      // Only show load message if there's no success message from navigation state
      if (!navigationState.successMessage) {
        setGeneralErrors([]);
      }
      setIsDirty(false);
      hasLoadedInitialData.current = true;

      // 初期値を更新（変更検知の基準値として使用）
      // localStorageから復元した場合は、DBデータを基準値とする（未保存判定のため）
      initialValuesRef.current = hasSavedData ? initialData : dataToLoad;
      console.log('[RestorePolicyPage] Updated initialValuesRef after data load');

      // reset()直後に変更追跡の基準値を更新
      setTimeout(() => {
        previousValuesRef.current = currentValuesRef.current;
        console.log('[RestorePolicyPage] Reset tracking refs after data load');
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, bcpDocument]);

  // Debug: Monitor values changes
  useEffect(() => {
    if (isEditMode && hasLoadedInitialData.current) {
      console.log('[RestorePolicyPage] Values changed after reset:', {
        keys: Object.keys(values),
        count: Object.keys(values).length,
        hasCompanyInfo: !!values.company_info,
        companyInfoLength: values.company_info?.length || 0
      });
    }
  }, [values, isEditMode]);

  useEffect(() => {
    const importantValue = values.restore_policy_important_business;
    if (!importantValue) {
      return;
    }

    const businesses = extractImportantBusinesses(importantValue);
    if (businesses.length === 0) {
      return;
    }

    const updatedFields = computeUpdatedFields(values, businesses);
    const diffEntries = Object.entries(updatedFields).filter(
      ([key, value]) => values[key] !== value
    );

    if (diffEntries.length > 0) {
      setValues(Object.fromEntries(diffEntries));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.restore_policy_important_business, setValues]);

  const handleFieldChange = (fieldId: string, value: string) => {
    console.log('[RestorePolicyPage] handleFieldChange called:', { fieldId, isDirty: false, willSetTo: true });
    setFieldValue(fieldId, value);
    setIsDirty(true);
    if (generalErrors.length > 0) {
      setGeneralErrors([]);
    }
  };

  const handleImportantBusinessChange = (fieldId: string, value: string) => {
    const businesses = extractImportantBusinesses(value);
    const nextValues = { ...values, [fieldId]: value };

    setFieldValue(fieldId, value);
    setIsDirty(true);

    if (businesses.length > 0) {
      const updatedFields = computeUpdatedFields(nextValues, businesses);
      const diffEntries = Object.entries(updatedFields).filter(
        ([key, updatedValue]) => nextValues[key] !== updatedValue
      );

      if (diffEntries.length > 0) {
        setValues(Object.fromEntries(diffEntries));
      }
    }

    if (generalErrors.length > 0) {
      setGeneralErrors([]);
    }
  };

  const handleSectionFieldChange = (fieldId: string, value: string) => {
    if (fieldId === 'restore_policy_important_business') {
      handleImportantBusinessChange(fieldId, value);
      return;
    }
    handleFieldChange(fieldId, value);
  };

  const handleSave = async () => {
    // 保存中の場合はスキップ
    if (isSaving) {
      return;
    }

    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      return;
    }

    setIsSaving(true);
    setSaveLoading(true);
    try {
      // 自動保存されたBCPがある場合はそのIDを使用、なければresolvedBcpIdを使用
      const bcpIdToSave = autoSavedBcp?.id || (resolvedBcpId === 'new' ? null : resolvedBcpId);

      const result = await bcpService.saveBasicSection(
        bcpIdToSave,
        'restore_policy',
        values,
        { current_step: 'restore_policy' },
        resolvedTenantId || undefined,
        'published'
      );

      const saveSuccessMessage = `保存が完了しました。`;

      setIsDirty(false);

      // Update bcpId if this was a new document
      if (resolvedBcpId === 'new' && result.id) {
        navigate(`/bcp/${result.id}/restore-policy`, {
          state: {
            ...location.state,
            bcpId: result.id,
            successMessage: saveSuccessMessage,
          },
          replace: true,
        });
      } else {
        // For existing BCP, reload to update status
        await reloadBCP();
        setSuccessMessage(saveSuccessMessage);
        setGeneralErrors([]);
      }
    } catch (error) {
      console.error('BCP保存エラー:', error);
      setGeneralErrors(['BCPの保存に失敗しました。']);
    } finally {
      setSaveLoading(false);
      setIsSaving(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'docx') => {
    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      return;
    }

    try {
      const response = await exportState.execute(async () => {
        const exportData = {
          type: 'restore_policy',
          sections: RESTORE_POLICY_SECTIONS.map((section) => ({
            title: section.title,
            fields: section.fields.map((field) => ({
              label: field.label,
              value: values[field.id] || '',
            })),
          })),
        };

        return await exportBCP(
          {
            ...values,
            export_data: JSON.stringify(exportData),
          },
          format
        );
      });

      if (response.success) {
        setSuccessMessage(
          `事業復旧方針を${format.toUpperCase()}形式でエクスポートしました`
        );
        setGeneralErrors([]);
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleExportPdf = async () => {
    // 保存中の場合はスキップ
    if (isSaving) {
      return;
    }

    setCurrentStep(3);

    // ダウンロード前に現在のフォーム内容と全セクションを保存
    setIsSaving(true);
    try {
      console.log('[RestorePolicyPage] Saving all sections before download...');
      const currentBcpId = autoSavedBcp?.id || resolvedBcpId;

      // Save current section and all draft sections from localStorage
      const finalBcpId = await saveAllDraftSections(
        currentBcpId,
        resolvedTenantId,
        bcpService,
        'restore_policy',
        values
      );

      console.log('[RestorePolicyPage] All sections saved successfully:', finalBcpId);
      setAutoSavedBcp({ id: finalBcpId });
      setIsDirty(false);
    } catch (error) {
      console.error('[RestorePolicyPage] Failed to save all sections:', error);
      setGeneralErrors(['ダウンロード前の保存に失敗しました。']);
      setIsSaving(false);
      return;
    } finally {
      setIsSaving(false);
    }

    setIsDownloadModalOpen(true);
  };

  const handleNext = async () => {
    // 「次へ」ボタンでのナビゲーション開始（ブロック解除）
    isNavigatingRef.current = true;

    // 保存中の場合はスキップ
    if (isSaving) {
      return;
    }

    // バリデーション
    const isValid = validate();
    if (!isValid) {
      setGeneralErrors(['入力内容に不備があります。各項目を確認してください。']);
      isNavigatingRef.current = false;
      return;
    }

    // BCP IDを決定
    let nextBcpId = autoSavedBcp?.id || resolvedBcpId;

    // 新規作成時またはフォームが編集されている場合は保存
    const shouldSave = resolvedBcpId === 'new' || isDirty;

    if (shouldSave) {
      setIsSaving(true);
      try {
        const bcpIdToSave = autoSavedBcp?.id || (resolvedBcpId !== 'new' ? resolvedBcpId : null);
        console.log('[RestorePolicyPage] Saving before navigation, bcpIdToSave:', bcpIdToSave);

        const result = await bcpService.saveBasicSection(
          bcpIdToSave,
          'restore_policy',
          values,
          { current_step: 'restore_policy' },
          resolvedTenantId || undefined,
          'published' // Save as published so it appears in BCP list
        );

        console.log('[RestorePolicyPage] Save successful, BCP ID:', result.id);
        nextBcpId = result.id;
        setAutoSavedBcp(result);
        setIsDirty(false);
      } catch (error) {
        console.error('[RestorePolicyPage] Save failed before navigation:', error);
        setGeneralErrors(['保存に失敗しました。再度お試しください。']);
        isNavigatingRef.current = false;
        setIsSaving(false);
        return;
      } finally {
        setIsSaving(false);
      }
    } else {
      console.log('[RestorePolicyPage] Form is clean and not new, navigating without saving');
    }

    // Build URL with tenantId if available
    let targetUrl = `/bcp/${nextBcpId}/regulation`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    console.log('[RestorePolicyPage] Navigating to:', targetUrl, 'with bcpId:', nextBcpId);
    navigate(targetUrl, {
      state: {
        ...location.state,
        restorePolicyData: values,
        restorePolicyFormData: values,
        fromPage: 'restore-policy',
        uploadedFiles,
        apiResponse,
        bcpId: nextBcpId,
      },
    });
  };

  const handleStepNavigate = async (targetStep: string) => {
    // Mark as internal navigation to bypass beforeunload
    isNavigatingRef.current = true;

    // Save current form data to localStorage before navigation
    if (resolvedBcpId !== 'new') {
      console.log('[RestorePolicyPage] Saving form data to localStorage before step navigation');
      saveBasicBcpSection(resolvedBcpId, resolvedTenantId, 'restore_policy', values);
    }

    // Build target URL with tenantId
    let targetUrl = `/bcp/${resolvedBcpId}/${targetStep}`;
    if (resolvedTenantId) {
      targetUrl += `?tenantId=${resolvedTenantId}`;
    }

    console.log('[RestorePolicyPage] Navigating to step:', targetStep, 'with state');

    // Navigate with complete state (same as "Next" button behavior)
    navigate(targetUrl, {
      state: {
        ...location.state,
        restorePolicyData: values,
        restorePolicyFormData: values,
        fromPage: 'restore-policy',
        uploadedFiles,
        apiResponse,
        bcpId: resolvedBcpId,
      },
    });
  };

  const aggregatedFieldErrors = useMemo(
    () => Object.values(fieldErrors),
    [fieldErrors]
  );

  const generationErrorInfo = useMemo(() => {
    if (!generationError) {
      return null;
    }

    const messages = [generationError];
    let title = '生成エラー';

    if (generationError.includes('タイムアウト')) {
      title = '⏱️ 処理タイムアウト';
      messages.push(
        '処理に5分以上かかっています。ページを再読み込みするか、時間を置いて再度お試しください。'
      );
    } else if (generationError.includes('Access denied')) {
      title = '🔒 アクセス権限エラー';
      messages.push('テナントへのアクセス権限がありません。管理者にお問い合わせください。');
    } else if (generationError.includes('Failed to create async job')) {
      title = '❌ ジョブ作成エラー';
      messages.push('AI生成ジョブの作成に失敗しました。時間を置いて再度お試しください。');
    }

    return {
      title,
      messages,
    };
  }, [generationError]);

  if (generationLoading || bcpLoading) {
    return (
      <div className="bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
              <Spinner size="lg" />
            </div>
            <h2 className="text-xl font-medium text-gray-900 mb-2">
              AIが復旧方針書を生成中...
            </h2>
            <p className="text-gray-600">
              アップロードされた資料を分析して、貴社の事業内容に応じた復旧方針書の下書きを作成しています。
            </p>

            {jobStatus && (
              <div className="mt-6 inline-block">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <span className="text-blue-700 font-medium">
                      {jobStatus === 'pending' && '⏳ 待機中'}
                      {jobStatus === 'processing' && '🔄 生成中'}
                    </span>
                  </div>
                  <div className="text-gray-700">
                    経過時間: <span className="font-mono font-semibold">{elapsedSeconds}</span>秒
                  </div>
                </div>

                <div className="w-64 mx-auto bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((elapsedSeconds / 120) * 100, 95)}%` }}
                  />
                </div>

                {elapsedSeconds > 60 && (
                  <p className="text-sm text-gray-500 mt-4">
                    💡 LLM処理には通常60-120秒かかります。そのままお待ちください。
                  </p>
                )}
              </div>
            )}

            {!jobStatus && (
              <p className="text-gray-600 mt-2">数十秒程度かかることがあります。</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <BCPFormLayout
      currentStep={2}
      showHeaderActions
      isEditMode={isEditMode}
      onSave={handleSave}
      onDownload={handleExportPdf}
      saveLoading={saveLoading}
      downloadLoading={exportState.isLoading}
      fieldContext={aiConsultContext.fieldContext}
      onAIContentApply={aiConsultContext.handleAIContentApply}
      bcpType="basic"
      bcpId={resolvedBcpId}
      topContent={
        <BasicBCPStepFlow
          currentStep="restore-policy"
          bcpId={resolvedBcpId}
          tenantId={resolvedTenantId}
          onStepNavigate={handleStepNavigate}
        />
      }
    >
      <BCPFormContainer
        header={
          <>
            <BCPPageTitle
              title="基本版BCPシート（復旧方針書）"
              isEditMode={isEditMode}
              editInfo={
                bcpDocument
                  ? {
                      title: bcpDocument.content.title,
                      id: bcpDocument.id,
                    }
                  : undefined
              }
            />
            <div className="mt-6 flex justify-center">
              <GuideTopAI />
            </div>
          </>
        }
        alerts={
          <div className="space-y-4">
            {successMessage && <Alert type="success" message={successMessage} />}

            {generalErrors.length > 0 && (
              <Alert
                type="error"
                title="エラーが発生しました"
                messages={generalErrors}
              />
            )}

            {aggregatedFieldErrors.length > 0 && (
              <Alert
                type="error"
                title="入力内容を確認してください"
                messages={aggregatedFieldErrors}
              />
            )}

            {generationErrorInfo && (
              <div className="space-y-3">
                <Alert
                  type="error"
                  title={generationErrorInfo.title}
                  messages={generationErrorInfo.messages}
                />
                <Button
                  variant="primary"
                  onClick={regenerate}
                  loading={generationLoading}
                  className="ml-2"
                >
                  再試行
                </Button>
              </div>
            )}

            {exportState.error && (
              <Alert
                type="error"
                title="エクスポートエラー"
                messages={[exportState.error]}
              />
            )}
          </div>
        }
        footer={
          <>
            <ReferenceMaterialsPanel uploadedFiles={uploadedFiles} className="mt-6" />
            <div className="mt-8 flex justify-center">
              <RedButton onClick={handleNext}>次へ</RedButton>
            </div>
          </>
        }
      >
        {RESTORE_POLICY_SECTIONS.map((section) => (
          <BCPSectionCard
            key={section.id}
            section={section}
            values={values}
            errors={fieldErrors}
            onChange={handleSectionFieldChange}
            onAIConsult={aiConsultContext.handleAIConsult}
          />
        ))}
      </BCPFormContainer>
    </BCPFormLayout>

    {/* Download Modal */}
    <BCPDownloadModal
      isOpen={isDownloadModalOpen}
      onClose={() => {
        setIsDownloadModalOpen(false);
        setCurrentStep(1);
      }}
      bcpType="basic"
      bcpId={autoSavedBcp?.id || bcpDocument?.id || (resolvedBcpId !== 'new' ? resolvedBcpId : null)}
    />
  </>
  );
};

export default RestorePolicyPage;
